import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, } from 'rxjs';
import { OnShiftClinician, Session, Shift, ShiftStatusResponse, ShiftsResponse } from '../../models/shift.model';
import { config } from 'config';
import { HttpClient } from '@angular/common/http';
import { ShiftAssignStatusEnum } from 'app/shared/helpers/status-options.helper';
import { delay, tap } from 'rxjs/operators';
import { createHttpParams } from 'app/shared/helpers/utils';

export interface GetShiftRequest {
  start_date: string;
  end_date: string;
}

export interface OrdersReportRequest {
  startTime: string; // Format: 'DD-MM-YYYY HH:mm:ss'
  endTime: string;
}

export interface TATMetrics {
  toFirstStart_p50: number | string | null;
  toFirstStart_p75?: number | string | null;
  toFirstStart_p95?: number | string | null;
  toActualStart_p50?: number | string | null;
  toActualStart_p75?: number | string | null;
  toActualStart_p95?: number | string | null;
  toFirstAssignment_p50?: number | string | null;
  toFirstAssignment_p75?: number | string | null;
  toFirstAssignment_p95?: number | string | null;
}

export interface OrdersReportData {
  totalIncome: number | null;
  weightLossIncome: number | null;
  receivedIncome: number | string | null; // sometimes comes as string
  consultsStarted: number | null;
  heNaTat: TATMetrics | null;
  heBatTat: TATMetrics | null;
  allNaTat: TATMetrics | null;
  allBatTat: TATMetrics | null;
  priorityTat: TATMetrics | null;
  pharmacyTat: TATMetrics | null;
  // Legacy fields for backward compatibility
  nextAvailableOrdersTAT: string | null; // returns "-"
  priorityOrdersTAT: string | null; // returns "-"
  pharmacyOrdersTAT: string | null; // returns "-"
  totalOrders: number | null;
  totalShiftHours: number | null;
  totalIdleTime: number | null;
  callNoAnswerOrders: number | null;
  clinicToReviewCount: number | null;
  waitinRoomOrderCount: number | null;
  ordersFromMorning: number | null;
  numberOfCliniciansOnShift: number | null;
  CTRWaitingTime: string | null; // e.g., "137 ± 189 mins"
  allocationAverageTime: string | null; // returns "-"
  averageConsultTime: string | number | null; // e.g., "8 ± 5 mins" or 0
  consultsPerHour: number | null;
}
export interface OrdersReportResponse {
  report: OrdersReportData;
  status: number;
}

export interface ImportedShift {
  row: number;
  clinicianId: number;
  clinicianName: string;
  shiftType: string;
  shiftId: number | null;
  startTime: string;
  endTime: string;
}

export interface FailedShiftImport {
  row: number;
  clinicianName: string;
  clinicianId: string;
  shiftType: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  reason: string;
}

export interface ImportShiftsResponse {
  success: boolean;
  dryRun: boolean;
  message: string;
  addedShifts: ImportedShift[];
  failedShifts: FailedShiftImport[];
}

@Injectable({
  providedIn: 'root',
})
export class ManageShiftsService {
  constructor(private http: HttpClient) { }

  // BehaviorSubject to hold and emit the latest shift status
  private shiftDetailsSubject = new BehaviorSubject<ShiftStatusResponse | null>(null);
  shiftDetails$ = this.shiftDetailsSubject.asObservable();

  getShifts(request: GetShiftRequest) {
    const params = createHttpParams(request);
    const url = config.apiUrl1 + 'clinician_shifts';
    return this.http.get<ShiftsResponse>(url, { params });
  }

  getOnShiftClinician(): Observable<OnShiftClinician[]> {
    const url = config.apiUrl1 + 'onShiftClinicians';
    return this.http.get<OnShiftClinician[]>(url)
  }

  getShift(id: number) {
    const url = `${config.apiUrl1}clinician_shift/${id}`;
    return this.http.get<any>(url);
  }

  findAvailableClinicians(session: Session): Observable<any> {
    const url = `${config.apiUrl1}clinician_shifts/get_available_doctors`;
    return this.http.post<any>(url, session);
  }

  createShift(shift: Shift) {
    const url = `${config.apiUrl1}clinician_shifts/store`;
    return this.http.post(url, shift);
  }

  updateShift(updatedShift: Shift): Observable<any> {
    const url = `${config.apiUrl1}clinician_shift/update/${updatedShift.shiftId}`;
    return this.http.post(url, updatedShift);
  }


  updateStatus(data: any): Observable<any> {
    const url = `${config.apiUrl1}clinician_shift/updateStatus`;
    return this.http.post<any>(url, data);
  }

  syncWithDeputy() {
    const url = `${config.apiUrl1}clinician_shift/sync`;
    return this.http.post<any>(url, {});
  }

  importShiftsFromCsv(formData: FormData): Observable<ImportShiftsResponse> {
    const url = `${config.apiUrl1}clinician_shifts/import_csv`;
    return this.http.post<ImportShiftsResponse>(url, formData);
  }


  getClinicianShiftStatus(): Observable<ShiftStatusResponse> {
    const url = `${config.apiUrl1}clinician/get/shiftStatus`;
    return this.http.get<ShiftStatusResponse>(url).pipe(
      tap(data => this.shiftDetailsSubject.next(data)))
  }


  isActionDisabledForClinician(): boolean {
    const shiftStatus = this.shiftDetailsSubject.value?.shiftStatus;
    return [ShiftAssignStatusEnum.Paused, ShiftAssignStatusEnum.Assigned].includes(shiftStatus);
  }

  getDashboardMetrics(payload: OrdersReportRequest): Observable<OrdersReportResponse> {
    const url = `${config.apiUrl1}ordersReport`;
    return this.http.post<OrdersReportResponse>(url, payload);
  }

}
